import type { SharedDriveMembershipMessageDatagramBuf } from "~/classes/generated/message"
import type { SharedDriveMembershipResponseDatagramBuf } from "~/classes/generated/response"
import type { SharedDriveMembershipRequestDatagram } from "~/models/polyschema/SharedDriveMembershipRequestDatagram"
import type { SharedDriveMembershipResponseDatagram } from "~/models/polyschema/SharedDriveMembershipResponseDatagram"

import { StaticFile } from "~/classes/file/StaticFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SharedDriveMembershipRequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { addSharedDriveIdToBuf, parseSharedDriveId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("sharedDrive/membership", theme.colors.teal.hex)

export function sendSharedDriveMembershipRequest(request: SharedDriveMembershipRequestDatagram) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const requestId = addRequestIdToBuf(builder, request.requestId)
  const sharedDriveId = addSharedDriveIdToBuf(builder, request.sharedDriveId)
  const home = addFileToBuf(builder, request.home)
  const archive = addFileToBuf(builder, request.archive)
  SharedDriveMembershipRequestDatagramBuf.startSharedDriveMembershipRequestDatagramBuf(builder)
  SharedDriveMembershipRequestDatagramBuf.addRequestId(builder, requestId)
  SharedDriveMembershipRequestDatagramBuf.addSharedDriveId(builder, sharedDriveId)
  SharedDriveMembershipRequestDatagramBuf.addHome(builder, home)
  SharedDriveMembershipRequestDatagramBuf.addArchive(builder, archive)
  SharedDriveMembershipRequestDatagramBuf.addOffline(builder, request.offline)
  const buf = SharedDriveMembershipRequestDatagramBuf.endSharedDriveMembershipRequestDatagramBuf(builder)

  RequestDatagramBuf.startRequestDatagramBuf(builder)
  RequestDatagramBuf.addSharedDriveMembershipRequest(builder, buf)
  const requestDatagram = RequestDatagramBuf.endRequestDatagramBuf(builder)

  const requestBuf = DatagramBuf.createRequestVector(builder, [requestDatagram])
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, requestBuf)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

export function handleSharedDriveMembershipResponseDatagramBuf(datagram: SharedDriveMembershipResponseDatagramBuf) {
  onSharedDriveMembershipResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    sharedDriveId: parseSharedDriveId(datagram.sharedDriveId()!),
    home: maybe(parseFile)(datagram.home()),
    archive: maybe(parseFile)(datagram.archive()),
    offline: datagram.offline(),
  })
}

export function onSharedDriveMembershipResponse(response: SharedDriveMembershipResponseDatagram) {
  const { emitInterfaceEvent, emitEntityEvent } = useEvents()

  emitInterfaceEvent("sharedDriveMembershipResponse", response, response.requestId)

  if (response.error)
    return onSharedDriveMembershipErrored(response.error)
  if (!response.home || !response.archive)
    throw new Error(" No home / archive info in response")

  console.debug("Membership updated successfully", response)

  emitEntityEvent("fileProperties", response.home.id, response.home, response.offline)
  emitEntityEvent("fileProperties", response.archive.id, response.archive, response.offline)
}

export function onSharedDriveMembershipErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()

  console.error("Shared drive invite errored", error)
  notifyDatagramError(error, "Failed to update shared drive membership")
}

export function handleSharedDriveMembershipMessageDatagramBuf(
  datagram: SharedDriveMembershipMessageDatagramBuf,
) {
  onSharedDriveMembershipMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    sharedDriveId: parseSharedDriveId(datagram.sharedDriveId()!),
    home: parseFile(datagram.home()!),
    archive: parseFile(datagram.archive()!),
  })
  return console.error("No handler for membership messages yet")
}

export function onSharedDriveMembershipMessage(message: SharedDriveMembershipMessageDatagram) {
  const { emitEntityEvent } = useEvents()
  const { onSharedDriveMembershipUpdated } = useSharedDrives()

  const { sharedDriveId, home, archive } = message
  onSharedDriveMembershipUpdated({
    sharedDriveId,
    home: new StaticFile(home),
    archive: new StaticFile(archive),
  })

  emitEntityEvent("fileProperties", home.id, home, false)
  emitEntityEvent("fileProperties", archive.id, archive, false)
}
